/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
export const gettingStartedContent = `
## Welcome to Dexter

Dexter is a data transformation platform that allows you to visually design and execute transformations between different data formats to support data interoperability.

### Objective
* The U.S. Navy requires the development of a Domain Specific Model (DSM) for the Integrated Combat System (ICS). The current process to transform code is costly in both time and funding. 
* The DSM development enables development of common components reducing the cost, complexity, and timeline for integration. 

### What is Dexter?

Dexter provides a visual, node-based interface for creating data transformation pipelines. With Dexter, you can:

- Upload input files (XML, Protobuf, etc.)
- Design transformation logic using a visual flow editor
- Generate transformed output files
- Save and reuse transformation mappings
- Create and edit collections of logically grouped files

### Key Concepts
* **Input Collection**: Data files uploaded together which share a relationship.
* **Input Node**: A data structure housing parsed data from an uploaded data file. It takes in no inputs.
* **Transform Node**: A two-way node that takes in data, performs a transform on that data, and outputs the result to another node.
* **Output Node**: A node that contains the data produced from a transform node.
* **Composer Node**: A node that contains the data produced from a transform node and which also allows output file customizability.
* **Transform**: At least one data input node hooked into at least one transform algorithm, which is in turn hooked into at least one output containing transformed data.
* **Mapping**: A set of one or more transforms.
* **Recommendation**: Given a context and an input collection, Dexter’s AI features will provide recommendations for transform algorithms.
* **Granular Transform**: Transformation of individual pieces of data from an input node instead of the entire data file.
* **Transform Family**: A subset of transform algorithms specific to set input data and output data.
* **PX Transform**: A class of transforms containing algorithms specific to transforming Protobuf to XML data.
  * **PX-PRIM**: A transformation algorithm (reflected in a transform node) that converts a Protobuf primitive granular to its XML representation.
  * **PX-MESS**: A transformation algorithm (reflected in a transform node) that converts a Protobuf message granular to its XML representation.
* **XP Transform**: A class of transforms containing algorithms specific to transforming XML to Protobuf data.
  * **XP-PRIM**: A transformation algorithm (reflected in a transform node) that converts a XML primitive granular to its Protobuf representation.
  * **XP-MESS**: A transformation algorithm (reflected in a transform node) that converts a XML message granular to its Protobuf representation.
* **Builder**: Functionality built into Dexter that handles the composition of multiple pieces of granular data into a coherent output file for user download.


### Quick Start

1. Upload files in the initialization modal or drag an input file onto the canvas
2. Add a transform node from the sidebar
3. Connect your input to the transform node
4. Add an output node and connect it to the transform
5. Download your transformed data

### Need Help?

Explore the other sections in this documentation to learn more about specific features and workflows.
`;
